import Phaser from 'phaser';
import { ARCHETYPE_LABEL } from '../data/subspecs';
import type { CombatState } from '../sim/combat';
import { drawHero, drawShapeBoard } from './drawHero';
import { heroFigure } from './heroView';
import type { GameController } from '../ui/controller';
import { watchGameKeyboard } from '../ui/gameKeyboard';

interface Floater {
  label: Phaser.GameObjects.Text;
  life: number;
  vy: number;
}

export class FieldScene extends Phaser.Scene {
  private readonly controller: GameController;
  private backdrop!: Phaser.GameObjects.Graphics;
  private figures!: Phaser.GameObjects.Graphics;
  private hud!: Phaser.GameObjects.Text;
  private sub!: Phaser.GameObjects.Text;
  private floaters: Floater[] = [];
  private acc = 0;
  private seenTick = -1;
  private lunge = 0;
  private reduceMotion = false;

  constructor(controller: GameController) {
    super('field');
    this.controller = controller;
  }

  create(): void {
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.backdrop = this.add.graphics();
    this.figures = this.add.graphics();
    this.hud = this.add.text(18, 16, '', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '20px',
      color: '#f6ecd9',
      fontStyle: '600',
    });
    this.sub = this.add.text(18, 42, '', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '14px',
      color: '#b5a48f',
    });
    this.hud.setDepth(5);
    this.sub.setDepth(5);
    this.input.on('pointerdown', () => {
      this.controller.retryQueuedClear();
    });
    if (this.input.keyboard) watchGameKeyboard(this.controller, this.input.keyboard);
  }

  update(_time: number, delta: number): void {
    const speed = this.controller.watchSpeed;
    if (this.controller.fight) {
      this.acc += delta * speed;
      let guard = 0;
      while (this.acc >= 250 && this.controller.fight && guard < 8) {
        this.acc -= 250;
        this.controller.stepFight();
        guard += 1;
      }
    } else if (this.controller.readyForNext(performance.now())) {
      this.controller.startFight();
      this.seenTick = -1;
    }

    const now = this.time.now / 1000;
    if (!this.reduceMotion) this.lunge = Math.max(0, this.lunge - delta / 140);
    this.redraw(now);
  }

  private redraw(time: number): void {
    const width = this.scale.width;
    const height = this.scale.height;
    const fight = this.controller.fight;
    const save = this.controller.save.data;
    const figure = heroFigure(save.hero.build, save.gear.equipped, save.hero.level);
    const zone = fight?.stageId ? zoneLook(fight) : zoneLookFromId(save.progress.pushStage);
    const heroMode = this.controller.tab !== 'combat';

    this.backdrop.clear();
    this.backdrop.fillStyle(zone.sky, 1);
    this.backdrop.fillRect(0, 0, width, height);
    this.backdrop.fillStyle(zone.mist, 0.18);
    this.backdrop.fillRect(0, height * 0.42, width, height * 0.2);
    this.backdrop.fillStyle(zone.ground, 1);
    this.backdrop.fillRect(0, height * 0.72, width, height * 0.28);
    this.backdrop.lineStyle(2, 0xe7c27a, 0.25);
    this.backdrop.lineBetween(24, height * 0.74, width - 24, height * 0.74);

    this.figures.clear();
    if (typeof location !== 'undefined' && new URLSearchParams(location.search).get('shapes') === '1') {
      drawShapeBoard(this.figures, width, height);
      this.hud.setText('Placeholder shapes');
      this.sub.setText('T1 ragged wraps · T2 boiled leather · T3 riveted plates · T4 matched pauldrons');
      return;
    }
    const bob = this.reduceMotion ? 0 : Math.sin(time * 2) * 3;
    const feetY = height * (heroMode ? 0.8 : 0.76) + bob;
    const hx = heroMode ? width * 0.5 : width * 0.3;
    const scale = heroMode ? Math.min(1.85, width / 380) : Math.min(1.5, width / 460);
    drawHero(this.figures, figure, hx, feetY, scale, time, this.lunge * 18);

    if (!heroMode && fight) {
      this.drawEnemies(fight, width, feetY);
      this.spawnHits(fight, hx, feetY, width);
      const hp = `${Math.max(0, Math.ceil(fight.hero.hp))} / ${fight.hero.maxHp}`;
      this.hud.setText(fight.isWall ? `${fight.stageName}  ·  Wall` : fight.stageName);
      this.sub.setText(`${fight.zoneName}   HP ${hp}${fight.hero.shield > 0 ? `   guard ${Math.ceil(fight.hero.shield)}` : ''}`);
    } else {
      const heart = figure.heart ? ARCHETYPE_LABEL[figure.heart] : 'Unbound';
      this.hud.setText(save.hero.name);
      this.sub.setText(`Heart Mark · ${heart}`);
    }

    for (const floater of this.floaters) {
      floater.life -= 1;
      floater.label.y += floater.vy;
      floater.label.alpha = Math.max(0, floater.life / 28);
    }
    this.floaters = this.floaters.filter((floater) => {
      if (floater.life > 0) return true;
      floater.label.destroy();
      return false;
    });
  }

  private drawEnemies(fight: CombatState, width: number, feetY: number): void {
    const living = fight.enemies.filter((enemy) => enemy.alive && enemy.hp > 0);
    const g = this.figures;
    const swarm = living.length > 8;
    const cols = swarm ? Math.max(8, Math.floor((width * 0.46) / 22)) : living.length;
    living.forEach((enemy, index) => {
      const boss = enemy.role === 'boss';
      const col = swarm ? index % cols : index;
      const row = swarm ? Math.floor(index / cols) : 0;
      const w = swarm ? 16 : boss ? 78 : 40;
      const h = swarm ? 16 : boss ? 110 : 58;
      const x = width * (swarm ? 0.5 : 0.58) + col * (swarm ? 22 : boss ? 96 : 64);
      const y = feetY - h - row * (swarm ? 22 : 0);
      g.fillStyle(boss ? 0x4a5c78 : 0x6a4558, 1);
      g.fillRoundedRect(x, y, w, h, swarm ? 3 : boss ? 10 : 12);
      g.fillStyle(0x7fd1c4, 0.85);
      g.fillCircle(x + w * 0.7, y + (swarm ? 6 : 22), swarm ? 2 : boss ? 5 : 3);
      if (boss && enemy.stacks > 0) {
        const plates = Math.min(6, enemy.stacks);
        for (let plate = 0; plate < plates; plate += 1) {
          g.fillStyle(0xd5dde6, 0.85);
          g.fillRoundedRect(x - 8, y + 16 + plate * 12, 14, 8, 2);
        }
      }
      const ratio = Math.max(0, enemy.hp / enemy.maxHp);
      g.fillStyle(0x1a1018, 1);
      g.fillRect(x, y - 12, w, 6);
      g.fillStyle(enemy.role === 'boss' ? 0xe07a72 : 0xe7c27a, 1);
      g.fillRect(x, y - 12, w * ratio, 6);
    });
  }

  private spawnHits(fight: CombatState, hx: number, feetY: number, width: number): void {
    if (fight.tick === this.seenTick) return;
    this.seenTick = fight.tick;
    const living = fight.enemies.filter((enemy) => enemy.hp > 0 || fight.lastHits.some((hit) => hit.who === enemy.id));
    for (const hit of fight.lastHits) {
      let x = hx;
      let y = feetY - 160;
      if (hit.who !== 'hero') {
        const index = living.findIndex((enemy) => enemy.id === hit.who);
        x = width * 0.62 + Math.max(0, index) * 64;
        y = feetY - 120;
        this.lunge = 1;
      }
      const color = hit.who === 'hero' ? '#e07a72' : '#f6ecd9';
      const label = this.add.text(x, y, hit.text, {
        fontFamily: 'Source Sans 3, sans-serif',
        fontSize: '16px',
        color,
        fontStyle: '700',
      });
      label.setDepth(6);
      this.floaters.push({ label, life: 28, vy: -0.7 });
    }
  }
}

function zoneLook(fight: CombatState): { sky: number; ground: number; mist: number } {
  if (fight.zoneName === 'Rope Span') return { sky: 0x2a1c18, ground: 0x4a3424, mist: 0x8a5a3a };
  if (fight.zoneName === 'Ironhide Gate') return { sky: 0x161c28, ground: 0x2c3442, mist: 0x8ea0b5 };
  if (fight.zoneName === 'Mite Gallery') return { sky: 0x1a2414, ground: 0x314028, mist: 0x9aaa72 };
  if (fight.zoneName === 'Last Bell') return { sky: 0x2c1420, ground: 0x3a2430, mist: 0xc47a6a };
  return { sky: 0x24182c, ground: 0x3a2a28, mist: 0x6d4a6e };
}

function zoneLookFromId(stageId: number): { sky: number; ground: number; mist: number } {
  if (stageId >= 12) return { sky: 0x2c1420, ground: 0x3a2430, mist: 0xc47a6a };
  if (stageId >= 11) return { sky: 0x1a2414, ground: 0x314028, mist: 0x9aaa72 };
  if (stageId >= 9) return { sky: 0x161c28, ground: 0x2c3442, mist: 0x8ea0b5 };
  if (stageId >= 5) return { sky: 0x2a1c18, ground: 0x4a3424, mist: 0x8a5a3a };
  return { sky: 0x24182c, ground: 0x3a2a28, mist: 0x6d4a6e };
}
